import { DateTime } from "luxon";

import { blastFiringAjax } from "../../../ajax/BlastFiringAjax";
import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { rigMetersAjax } from "../../../ajax/RigMetersAjax";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { TargetCategoryName } from "../../../types/TargetCategory";
import { TripleShift } from "../../../types/Shift";

const LIVE = {
  refetchInterval: 30_000,
  refetchIntervalInBackground: false,
} as const;

/**
 * The cards that read a dataset and the Targets card that compares against it share one cache entry,
 * so the fetch belongs here rather than in whichever card happens to be on screen. Tiles are granted
 * one by one, so a card declaring its own copy would go unpolled whenever the owning tile is hidden.
 */
export const plantStatusQuery = (date: DateTime, shift: TripleShift, fullDay: boolean) => ({
  queryKey: ["dashboard", "plantStatus", date.toISODate(), shift, ...(fullDay ? ["full-day"] : [])],
  queryFn: async () => {
    const res = await dashboardsAjax.getPlantStatusCardData(date, shift, fullDay);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
  ...LIVE,
});

// Note: drilling and blasting change by the shift rather than the minute, so this one is not polled, and a caller with
// neither drill nor blast data gets null rather than an error.
export const drillBlastQuery = (date: DateTime, category: TargetCategoryName) => ({
  queryKey: ["dashboard", "drillBlast", date.toISODate(), category],
  queryFn: async () => {
    const res = await dashboardsAjax.getDrillBlastCardData(date, category);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
  staleTime: 5 * 60_000,
});

// Note: not polled either, as the formula actuals also change by the shift.
export const targetActualsQuery = (date: DateTime) => ({
  queryKey: ["dashboard", "targetActuals", date.toISODate()],
  queryFn: async () => {
    const res = await dashboardsAjax.getTargetsCardActuals(date);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
  staleTime: 5 * 60_000,
});

export const targetsQuery = (date: DateTime, category: TargetCategoryName) => ({
  queryKey: ["dashboard", "targets", date.toISODate(), category],
  queryFn: async () => {
    const res = await dashboardsAjax.getTargetsCardData(date, category);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
});

export const blastsTodayQuery = (date: DateTime) => ({
  queryKey: ["dashboard", "blastsToday", date.toISODate()],
  queryFn: async () => {
    const res = await dashboardsAjax.getBlastsToday(date);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
  staleTime: 60_000,
});

export const serviceDueQuery = {
  queryKey: ["dashboard", "rigServiceDue"],
  queryFn: async () => {
    const res = await rigMetersAjax.getServiceDue();
    return ErrorResponse.isErrorResponse(res) ? null : res.data;
  },
  staleTime: 5 * 60_000,
};

export const openMisfiresQuery = {
  queryKey: ['drillBlast', 'openMisfires'],
  queryFn: async () => {
    const res = await blastFiringAjax.getOpenMisfires();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load open misfires');
    return res.data;
  },
  staleTime: 60_000,
};

export const materialMovementQuery = (date: DateTime, shift: TripleShift) => ({
  queryKey: ["dashboard", "materialMovement", date.toISODate(), shift],
  queryFn: async () => {
    const res = await dashboardsAjax.getMaterialMovementCardData(date, shift);
    return ErrorResponse.isErrorResponse(res) ? null : res;
  },
  ...LIVE,
});
